// MUI
import { Stack } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CellTowerIcon from '@mui/icons-material/CellTower';
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';

// Utils
import { secondsToDhms } from '../../../utils';
import useDateTimeFormat from '../../../hooks/useDateTimeFormat';

// Types
import { Reader } from '../../../types/Reader';
import { ReaderOpsStatus } from '../../../types/ReaderOpsStatus';
import { LocationMethod } from '../../../types/LocationMethod';
import { LocationMehod, ReaderOwnerAccountId } from '../../../constants/Constants';

// Redux
import { SettingsState } from '../../../redux/slices/settingsSlice';
import { useAppSelector } from '../../../redux/hook';

// Components
import DisplayLabels from '../labels/DisplayLabels';
import { OverviewCard, OverviewGrid, OverviewInline, GridField } from '../../common/OverviewCard/OverviewCard';

interface ReaderStageProp {
  reader: Reader;
  settings?: SettingsState;
}

export default function ReaderStage(props: ReaderStageProp) {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();
  const { reader } = props;
  const { opsStatusColor, supplyChainStatusColor } = useAppSelector((state) => state.settings);

  //  Helpers
  const renderLocationMethod = (locationMethod: LocationMethod) => {
    if (locationMethod === LocationMehod.GPS) return <SatelliteAltOutlinedIcon style={{ color: 'green', fontSize: '1.2rem' }} />;
    if (locationMethod === LocationMehod.NETWORK) return <CellTowerIcon style={{ color: 'blue', fontSize: '1.2rem' }} />;
    return null;
  };

  const renderBatteryPercent = (pct: number) => {
    const cls = pct >= 80 ? 'overview-value--ok' : pct <= 20 ? 'overview-value--bad' : pct <= 30 ? 'overview-value--warn' : '';
    return <span className={cls}>{pct}%</span>;
  };

  const getOpsStatusColor = (statusId: ReaderOpsStatus) => opsStatusColor?.value.find((v) => v.text === statusId)?.value ?? '';
  const getSupplyChainStatusColor = (statusId: number) => supplyChainStatusColor?.value.find((v) => v.text === statusId)?.value ?? '';

  const ownershipMap = Object.entries(ReaderOwnerAccountId).reduce(
    (acc, [key, value]) => {
      const readableKey = key.replace(/([A-Z])/g, ' $1').trim();
      acc[value as string] = readableKey;
      return acc;
    },
    {} as Record<string, string>,
  );

  const accountId = reader.accountId ?? '';
  const ownershipLabel = ownershipMap[accountId];

  // Computed values
  const readerTypeValue = (
    <OverviewInline>
      <span>{reader.intendedUseDescr ?? '-'}</span>
      {reader.lockedLocation && <LockIcon style={{ color: 'green', fontSize: '1rem' }} />}
    </OverviewInline>
  );

  const nextReportValue = reader?.expectedReportDateTime
    ? (() => {
        const diffInSeconds = (new Date(reader.expectedReportDateTime).getTime() - new Date().getTime()) / 1000;
        return diffInSeconds > 0 ? secondsToDhms(diffInSeconds) : 'Overdue';
      })()
    : '-';

  const locationMethodValue = (
    <OverviewInline>
      {reader.locationMethod && renderLocationMethod(reader.locationMethod)}
      <span>{reader.locationMethodDescr}</span>
      <span style={{ color: '#6b7280' }}>({Math.floor((reader.stdDevCentroid ?? 0) * 1000)}m)</span>
    </OverviewInline>
  );

  const labelsValue =
    reader.labels && reader.labels.length > 0 ? (
      <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
        <DisplayLabels labels={reader.labels} />
      </Stack>
    ) : (
      '-'
    );

  // Grid rows: 4 columns, same shape as DeviceStage
  const rows: GridField[][] = [
    [
      { label: 'Reader Type', value: readerTypeValue },
      { label: 'Reporting Frequency', value: reader.reportInterval ? secondsToDhms(reader.reportInterval) : '-' },
      { label: 'Next Report In', value: nextReportValue },
      { label: 'Battery', value: reader.batteryPercent != null ? renderBatteryPercent(reader.batteryPercent) : '-' },
    ],
    [
      { label: 'Station', value: reader.stationIataCode ?? '-' },
      { label: 'Facility', value: reader.facilityName ?? '-' },
      { label: 'Plugged In', value: reader.isChargerPluggedIn ? 'Yes' : 'No' },
      {
        label: 'Supply Chain Status',
        value: (
          <span style={{ fontWeight: 700, color: getSupplyChainStatusColor(reader.supplyChainStatus) }}>{reader.supplyChainStatusDescr ?? '-'}</span>
        ),
      },
    ],
    [
      {
        label: 'Operational Status',
        value: <span style={{ fontWeight: 700, color: getOpsStatusColor(reader.opsStatus) }}>{reader.opsStatusDescr ?? '-'}</span>,
      },
      { label: 'Location Method', value: locationMethodValue },
      { label: 'Responsible', value: reader.responsibleUserFullName ?? '-' },
      { label: 'Ownership', value: ownershipLabel ?? '-' },
    ],
    [
      { label: withTimeMode('Last Seen'), value: toStandardDate(reader.lastSeenDateTime) || '-' },
      { label: 'Device Labels', value: labelsValue, valueClassName: 'overview-value--wrap' },
    ],
  ];

  return (
    <OverviewCard
      title='Reader Details'
      headerRight={
        <>
          <span style={{ color: '#6b7280' }}>Name:</span>
          <strong style={{ color: '#1f2229' }}>{reader.name ?? '-'}</strong>
        </>
      }
      className='extra-margin'
    >
      <OverviewGrid columns={4} rows={rows} />
    </OverviewCard>
  );
}
